import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'react-hot-toast';
import { Plus, Edit, DollarSign, Percent, Tag, Truck, Calculator, Loader2, AlertCircle, Shield } from 'lucide-react';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ConditionTypes = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('pricing');
  const [viewItem, setViewItem] = useState(null);
  const [conditionTypes, setConditionTypes] = useState([]);
  const [accessSequences, setAccessSequences] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({
    condition_type_id: '',
    description: '',
    condition_class: 'A',
    calculation_type: 'C',
    condition_category: 'BASE',
    plus_minus: '+',
    access_sequence_id: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [typesRes, seqRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/pricing/condition-types`),
        axios.get(`${BACKEND_URL}/api/pricing/access-sequences`)
      ]);
      setConditionTypes(typesRes.data || []);
      setAccessSequences(seqRes.data || []);
    } catch (error) {
      console.error('Error loading condition types:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to load condition types: ${errorMsg}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (ct = null) => {
    if (ct) {
      setEditingType(ct);
      setFormData({
        condition_type_id: ct.condition_type_id,
        description: ct.description,
        condition_class: ct.condition_class,
        calculation_type: ct.calculation_type,
        condition_category: ct.condition_category,
        plus_minus: ct.plus_minus,
        access_sequence_id: ct.access_sequence_id || ''
      });
    } else {
      setEditingType(null);
      setFormData({
        condition_type_id: '',
        description: '',
        condition_class: 'A',
        calculation_type: 'C',
        condition_category: 'BASE',
        plus_minus: '+',
        access_sequence_id: ''
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.condition_type_id || formData.condition_type_id.trim().length === 0) {
      toast.error('Condition Type ID is required');
      return;
    }
    if (formData.condition_type_id.length > 4) {
      toast.error('Condition Type ID must be max 4 characters');
      return;
    }
    if (!formData.description || formData.description.trim().length === 0) {
      toast.error('Description is required');
      return;
    }
    
    setIsSaving(true);
    try {
      const data = {
        ...formData,
        condition_type_id: formData.condition_type_id.toUpperCase().trim(),
        description: formData.description.trim(),
        access_sequence_id: formData.access_sequence_id || null
      };

      if (editingType) {
        await axios.put(`${BACKEND_URL}/api/pricing/condition-types/${editingType.condition_type_id}`, data);
        toast.success(`Condition Type '${editingType.condition_type_id}' updated successfully`);
      } else {
        await axios.post(`${BACKEND_URL}/api/pricing/condition-types`, data);
        toast.success(`Condition Type '${data.condition_type_id}' created successfully`);
      }
      setIsDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Error saving condition type:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to save condition type: ${errorMsg}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (conditionTypeId) => {
    if (!window.confirm(`Are you sure you want to delete Condition Type '${conditionTypeId}'? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${BACKEND_URL}/api/pricing/condition-types/${conditionTypeId}`);
      toast.success(`Condition Type '${conditionTypeId}' deleted successfully`);
      loadData();
    } catch (error) {
      console.error('Error deleting condition type:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to delete condition type: ${errorMsg}`);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'BASE': return <DollarSign className="w-4 h-4" />;
      case 'DISC': return <Percent className="w-4 h-4" />;
      case 'SURCH': return <Tag className="w-4 h-4" />;
      case 'FREIGHT': return <Truck className="w-4 h-4" />;
      case 'TAX': return <Calculator className="w-4 h-4" />;
      case 'INSUR': return <Shield className="w-4 h-4" />;
      default: return <DollarSign className="w-4 h-4" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'BASE': return 'bg-orange-100 text-orange-800';
      case 'DISC': return 'bg-green-100 text-green-800';
      case 'SURCH': return 'bg-orange-100 text-orange-800';
      case 'FREIGHT': return 'bg-orange-100 text-orange-800';
      case 'TAX': return 'bg-red-100 text-red-800';
      case 'INSUR': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Condition Types</h1>
            <p className="text-sm text-gray-600 mt-1">Configure pricing condition types (PR00, K004, K005, MWST, etc.)</p>
          </div>
          {writable && (<Button onClick={() => handleOpenDialog()} data-testid="add-condition-type-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Condition Type
          </Button>)}
        </div>

        <Card className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
              <span className="ml-3 text-gray-600">Loading condition types...</span>
            </div>
          ) : conditionTypes.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">No condition types found.</p>
              <p className="text-sm text-gray-400 mt-1">Click "Add Condition Type" to create one.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="condition-types-table">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Type ID</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Description</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Category</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Calc Type</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">+/-</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Access Seq</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {conditionTypes.map((ct) => (
                    <tr key={ct.condition_type_id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 font-mono font-bold">{ct.condition_type_id}</td>
                      <td className="py-3 px-4">{ct.description}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(ct.condition_category)}`}>
                          {getCategoryIcon(ct.condition_category)}
                          {ct.condition_category}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {ct.calculation_type === 'A' ? 'Percentage' : 
                         ct.calculation_type === 'B' ? 'Fixed' : 
                         ct.calculation_type === 'C' ? 'Per Qty' :
                         ct.calculation_type === 'D' ? 'Gross Wt' :
                         ct.calculation_type === 'E' ? 'Net Wt' :
                         ct.calculation_type === 'F' ? 'Volume' : ct.calculation_type}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`font-bold ${ct.plus_minus === '+' ? 'text-green-600' : 'text-red-600'}`}>
                          {ct.plus_minus}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-mono text-sm">{ct.access_sequence_id || '-'}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex justify-end gap-1">
                          <RecordActions doctype="pricing" onView={() => setViewItem(ct)} onEdit={() => handleOpenDialog(ct)} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingType ? 'Edit Condition Type' : 'Create Condition Type'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="condition_type_id">Condition Type ID * (max 4 chars)</Label>
                  <Input
                    id="condition_type_id"
                    value={formData.condition_type_id}
                    onChange={(e) => setFormData({...formData, condition_type_id: e.target.value.toUpperCase()})}
                    placeholder="e.g., PR00, K004"
                    maxLength={4}
                    disabled={!!editingType}
                    className="uppercase"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="e.g., Base Price, Material Discount"
                    maxLength={100}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Condition Class *</Label>
                  <Select value={formData.condition_class} onValueChange={(v) => setFormData({...formData, condition_class: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A">A - Prices</SelectItem>
                      <SelectItem value="B">B - Discounts/Surcharges</SelectItem>
                      <SelectItem value="D">D - Taxes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Calculation Type *</Label>
                  <Select value={formData.calculation_type} onValueChange={(v) => setFormData({...formData, calculation_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A">A - Percentage</SelectItem>
                      <SelectItem value="B">B - Fixed Amount</SelectItem>
                      <SelectItem value="C">C - Per Quantity</SelectItem>
                      <SelectItem value="D">D - Per Gross Weight</SelectItem>
                      <SelectItem value="E">E - Per Net Weight</SelectItem>
                      <SelectItem value="F">F - Per Volume</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Category *</Label>
                  <Select value={formData.condition_category} onValueChange={(v) => setFormData({...formData, condition_category: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BASE">Base Price</SelectItem>
                      <SelectItem value="DISC">Discount</SelectItem>
                      <SelectItem value="SURCH">Surcharge</SelectItem>
                      <SelectItem value="FREIGHT">Freight</SelectItem>
                      <SelectItem value="TAX">Tax</SelectItem>
                      <SelectItem value="INSUR">Insurance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Plus/Minus *</Label>
                  <Select value={formData.plus_minus} onValueChange={(v) => setFormData({...formData, plus_minus: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="+">+ (Addition)</SelectItem>
                      <SelectItem value="-">- (Subtraction)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Access Sequence</Label>
                  <Select value={formData.access_sequence_id || '__NONE__'} onValueChange={(v) => setFormData({...formData, access_sequence_id: v === '__NONE__' ? '' : v})}>
                    <SelectTrigger><SelectValue placeholder="Select Access Sequence" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__NONE__">None</SelectItem>
                      {accessSequences.map(seq => (
                        <SelectItem key={seq.access_sequence_id} value={seq.access_sequence_id}>
                          {seq.access_sequence_id} - {seq.description}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-4 border-t mt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} disabled={isSaving}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSaving} data-testid="submit-condition-type">
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {editingType ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    editingType ? 'Update Condition Type' : 'Create Condition Type'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default ConditionTypes;
